// Validation + preflight for a company's own logo (Account.logo_url on the
// backend, surfaced as profileState.account_logo_url).
//
// Why a preflight instead of just handing the URL to the renderers:
// @react-pdf/renderer resolves <Image src> by fetching it during layout, has no
// onError hook, and a failed fetch rejects the whole document render — a dead
// logo URL would take down the worksheet preview, download, print, AND the
// quote-email attachment. So a URL is only ever passed on once it has been
// proven fetchable and of a format react-pdf can actually decode; otherwise the
// callers fall back to the existing text company name.

// @react-pdf/renderer v3 decodes PNG and JPEG only — no WebP, and SVG needs its
// separate <Svg> primitive (inline markup, not a URL). Restricting to these two
// also keeps SVG out of the copy-card HTML, which is injected with
// dangerouslySetInnerHTML.
export const SUPPORTED_LOGO_TYPES = ['image/png', 'image/jpeg']

// Characters that would break out of src="..." in the copy-card HTML string.
const UNSAFE_CHARS = /["'<>\s]/

// In development the logo is served over http from the Django dev server
// (http://localhost:8000/media/...) rather than S3, so those hosts are allowed
// alongside https. The value always comes from our own serializer/storage — never
// user-typed — and the UNSAFE_CHARS guard below still applies to every scheme, so
// the copy-card injection guarantee is unchanged. Non-local http (e.g.
// http://example.com) stays rejected.
const isLocalDevUrl = (url) =>
    url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')

export const isSafeLogoUrl = (url) => {
    if (typeof url !== 'string' || url.trim() === '') return false
    if (UNSAFE_CHARS.test(url)) return false
    return url.startsWith('https://') || isLocalDevUrl(url)
}

// Single-fetch preflight: returns the URL when it is a safe, fetchable, supported
// image; otherwise null so callers fall back to the company name. Any failure
// (bad status, unsupported type, network/CORS error) resolves to null — never
// throws — so a dead logo can't break a consumer.
export const probeLogoUrl = async (url) => {
    if (!isSafeLogoUrl(url)) return null

    try {
        const response = await fetch(url)
        if (!response.ok) return null

        // Strip any `; charset=...` parameter before comparing.
        const contentType = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase()
        return SUPPORTED_LOGO_TYPES.includes(contentType) ? url : null
    } catch {
        // Network failure or a CORS block — react-pdf's own fetch would fail the
        // same way, so treat it as "no logo".
        return null
    }
}
